import {
  Button,
  createListCollection,
  Field as ChakraField,
  Fieldset,
  HStack,
  Input,
  Text,
  VStack,
} from '@chakra-ui/react';
import ErrorsContainer from '@components/common/form/ErrorsContainer';
import { DraggablePanel } from '@components/ui/draggable';
import { Form } from '@components/ui/Form';
import RequiredIndicator from '@components/ui/RequiredIndicator';
import { CustomSelect, Option } from '@components/ui/select/Select';
import { createToast, ToastStatusEnum } from '@components/ui/toaster';
import { useAppSelector } from '@hooks/redux.hooks';
import useValidation from '@hooks/useValidation';
import { selectActiveGroupOrganization } from '@redux/authent/authent.selectors';
import { useCreateMapLayerMutation } from '@services/config/mapLayer.api';
import { useUpdateOrganizationMutation } from '@services/config/organization';
import { createErrorMessage } from '@utils/common.utils';
import { mapLayerSchema } from '@validations/mapLayer.schema';
import { Field, Formik } from 'formik';
import { JSX } from 'react';
import { BiSolidLayer } from 'react-icons/bi';
import { BsLayersHalf } from 'react-icons/bs';
import { FormattedMessage, useIntl } from 'react-intl';
import { v4 as uuidv4 } from 'uuid';

import { LayerTypeEnum, MapLayer } from '@/types/config/settings.types';
import { PopupPositionData } from '@/types/popup.types';

export const layerTypeIcons: Record<LayerTypeEnum, JSX.Element> = {
  [LayerTypeEnum.BASE_LAYER]: <BsLayersHalf />,
  [LayerTypeEnum.MESH]: <BiSolidLayer />,
};

export function MapLayerFormPopup({ mapConstraintsRef, top, left, onClose }: Readonly<PopupPositionData>) {
  const { formatMessage } = useIntl();

  const initialValues: Partial<MapLayer> = {
    code: uuidv4(),
    name: '',
    url: '',
    type: LayerTypeEnum.BASE_LAYER,
  };

  const validationSchema = useValidation(mapLayerSchema);
  const organization = useAppSelector(selectActiveGroupOrganization);

  const [mapLayerCreation, { isLoading: isLoadingMap, isError: isErrorMap, error: errorMap }] =
    useCreateMapLayerMutation();
  const [updateOrganization, { isLoading, isError, error }] = useUpdateOrganizationMutation();

  const apiErrorMessage = isError ? createErrorMessage(error) : isErrorMap ? createErrorMessage(errorMap) : undefined;

  return (
    <DraggablePanel.Root constraintsRef={mapConstraintsRef} top={top} left={left}>
      <DraggablePanel.Header onClose={onClose}>
        <FormattedMessage id="controls.toolMenu.maps.title" />
      </DraggablePanel.Header>
      <DraggablePanel.Body width={400}>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            mapLayerCreation(values as MapLayer)
              .unwrap()
              .then(() => {
                createToast(formatMessage({ id: 'controls.toolMenu.maps.successToast' }), ToastStatusEnum.SUCCESS);
                if (!organization) {
                  return;
                }
                updateOrganization({ ...organization, mapLayers: [...organization.mapLayers, values as MapLayer] })
                  .unwrap()
                  .then(() => {
                    createToast(
                      formatMessage({ id: 'controls.toolMenu.maps.organizationSuccessToast' }),
                      ToastStatusEnum.SUCCESS,
                    );
                    onClose();
                  });
              });
          }}
        >
          {({ errors, touched, handleChange, dirty, isValid, handleSubmit, values }) => (
            <Fieldset.Root invalid={Object.keys(errors).some((key) => touched[key as keyof typeof touched])}>
              <Form onSubmit={handleSubmit} style={{ width: '100%' }}>
                <Fieldset.Content
                  width="100%"
                  backgroundColor="neutral.700"
                  flexGrow={1}
                  alignItems="start"
                  gap={0}
                  padding={4}
                >
                  <VStack gap={2} width="100%">
                    <ChakraField.Root id="type" required invalid={touched.type && !!errors.type}>
                      <ChakraField.Label>
                        <Text>
                          <FormattedMessage id="controls.toolMenu.maps.type.type" />
                        </Text>
                        <RequiredIndicator />
                      </ChakraField.Label>
                      <CustomSelect
                        width="100%"
                        variant="darker"
                        collection={createListCollection({
                          items: Object.entries(LayerTypeEnum).map(([key, value]) => ({
                            label: formatMessage({ id: `controls.toolMenu.maps.type.${key}` }),
                            value: value,
                          })),
                        })}
                        formatLabel={(option: Option) => (
                          <HStack gap={1}>
                            {layerTypeIcons[option.value as LayerTypeEnum]}
                            <Text textStyle="md" color="neutral.white">
                              {option.label}
                            </Text>
                          </HStack>
                        )}
                        value={values.type}
                        onChange={(value) => value && handleChange('type')(value)}
                        defaultValue={LayerTypeEnum.BASE_LAYER}
                      />
                    </ChakraField.Root>
                    <ChakraField.Root required invalid={touched.name && !!errors.name}>
                      <ChakraField.Label>
                        <Text>
                          <FormattedMessage id="controls.toolMenu.maps.name" />
                        </Text>
                        <RequiredIndicator />
                      </ChakraField.Label>
                      <Field
                        as={Input}
                        id="name"
                        name="name"
                        type="text"
                        height="48px"
                        maxLength="50"
                        invalid={touched.name && !!errors.name}
                      />
                    </ChakraField.Root>
                    <ChakraField.Root required invalid={touched.url && !!errors.url}>
                      <ChakraField.Label>
                        <Text>
                          <FormattedMessage id="controls.toolMenu.maps.url" />
                        </Text>
                        <RequiredIndicator />
                      </ChakraField.Label>
                      <Field
                        as={Input}
                        id="url"
                        name="url"
                        type="text"
                        height="48px"
                        invalid={touched.url && !!errors.url}
                      />
                    </ChakraField.Root>
                  </VStack>
                </Fieldset.Content>
                {(Object.keys(errors).some((key) => touched[key as keyof typeof touched]) || isError || isErrorMap) && (
                  <Fieldset.ErrorText marginX={4}>
                    <ErrorsContainer errors={errors} touched={touched} apiError={apiErrorMessage} />
                  </Fieldset.ErrorText>
                )}
                <HStack justify="space-around" width="100%" padding={4} paddingTop={0} backgroundColor="neutral.700">
                  <Button variant="secondary" type="button" onClick={onClose} flex={1}>
                    <FormattedMessage id="global.cancel" />
                  </Button>
                  <Button
                    type="submit"
                    disabled={!dirty || !isValid || isLoading || isLoadingMap}
                    loading={isLoading || isLoadingMap}
                    flex={1}
                  >
                    <FormattedMessage id="global.create" />
                  </Button>
                </HStack>
              </Form>
            </Fieldset.Root>
          )}
        </Formik>
      </DraggablePanel.Body>
    </DraggablePanel.Root>
  );
}
